// SPDX-License-Identifier: AGPL-3.0-or-later
import { ExtensionUrl, } from '@intakeform/fhir';
let loadedEngine;
let loadingEngine;
/**
 * Load fhirpath.js and its R4 model (a separate chunk in the web bundle).
 * Cached; a failed load is retried on the next call.
 */
export function loadFhirPathEngine() {
    loadingEngine ??= Promise.all([import('fhirpath'), import('fhirpath/fhir-context/r4')]).then(([fhirpath, r4]) => {
        const engine = (resource, expression, questionnaire) => fhirpath.default.evaluate(resource, expression, { resource, questionnaire }, r4.default, {
            async: false,
        });
        loadedEngine = engine;
        return engine;
    }, (err) => {
        loadingEngine = undefined;
        throw err;
    });
    return loadingEngine;
}
/** The engine if {@link loadFhirPathEngine} has finished, else undefined. */
export function loadedFhirPathEngine() {
    return loadedEngine;
}
/** True when the form has any calculated item (so the engine is worth loading). */
export function hasCalculatedItems(questionnaire) {
    return calculatedItems(questionnaire).length > 0;
}
const FHIRPATH = 'text/fhirpath';
/** Far above any real scoring expression; bounds parse cost per item. */
export const MAX_CALCULATED_EXPRESSION_LENGTH = 4000;
/** The FHIRPath an item is calculated with, or undefined when it is not calculated. */
export function readCalculatedExpression(item) {
    const ext = item.extension?.find((e) => e.url === ExtensionUrl.calculatedExpression);
    const expr = ext?.valueExpression;
    if (expr?.language !== FHIRPATH || expr.expression === undefined)
        return undefined;
    if (expr.expression === '' || expr.expression.length > MAX_CALCULATED_EXPRESSION_LENGTH) {
        return undefined;
    }
    return expr.expression;
}
/** True when the item's answer is computed, so nobody may type it. */
export function isCalculatedItem(item) {
    return readCalculatedExpression(item) !== undefined;
}
/**
 * True for an item a patient must never see: a calculated item (scores are for
 * staff), or a group holding nothing else, such as a screener's office section.
 */
export function isHiddenFromPatient(item) {
    if (isCalculatedItem(item))
        return true;
    if (item.type !== 'group' || (item.item ?? []).length === 0)
        return false;
    return (item.item ?? []).every(isHiddenFromPatient);
}
/** Calculated items in document order (so one may read an earlier one). */
function calculatedItems(questionnaire) {
    const out = [];
    const collect = (items) => {
        for (const item of items ?? []) {
            if (isCalculatedItem(item))
                out.push(item);
            collect(item.item);
        }
    };
    collect(questionnaire.item);
    return out;
}
/** `response` without any answer for the given linkIds (containers with children kept). */
function withoutItems(items, drop) {
    return (items ?? []).flatMap((it) => {
        const next = { ...it };
        if (it.item !== undefined)
            next.item = withoutItems(it.item, drop);
        if (it.answer !== undefined) {
            next.answer = it.answer.map((a) => a.item === undefined ? a : { ...a, item: withoutItems(a.item, drop) });
        }
        if (!drop.has(it.linkId))
            return [next];
        if (next.item === undefined || next.item.length === 0)
            return [];
        delete next.answer;
        return [next];
    });
}
function evaluateOne(engine, expression, questionnaire, response) {
    // fhirpath.js may annotate its input; never hand it the caller's objects.
    try {
        const out = engine(structuredClone(response), expression, structuredClone(questionnaire));
        return out.length === 1 ? out[0] : undefined;
    }
    catch {
        return undefined;
    }
}
/** Coerce an expression result to the item's type, or undefined when it doesn't fit. */
function toCalculatedValue(item, raw) {
    switch (item.type) {
        case 'integer':
            return typeof raw === 'number' && Number.isInteger(raw) ? raw : undefined;
        case 'decimal':
            return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
        case 'boolean':
            return typeof raw === 'boolean' ? raw : undefined;
        case 'string':
        case 'text':
            return typeof raw === 'string' && raw !== '' ? raw : undefined;
        default:
            return undefined;
    }
}
function toAnswer(item, value) {
    if (typeof value === 'string')
        return { valueString: value };
    if (typeof value === 'boolean')
        return { valueBoolean: value };
    return item.type === 'decimal' ? { valueDecimal: value } : { valueInteger: value };
}
/**
 * Evaluate every calculated item against `response` (its calculated answers
 * already stripped). Each result is added to the working response before
 * the next item runs, so a band can read a total; repeated until stable so
 * order in the form does not matter. Returns values in document order.
 */
function compute(engine, questionnaire, response) {
    const calculated = calculatedItems(questionnaire);
    const values = new Map();
    const working = () => ({
        ...response,
        item: [
            ...(response.item ?? []),
            ...calculated.flatMap((item) => {
                const v = values.get(item.linkId);
                return v === undefined ? [] : [{ linkId: item.linkId, answer: [toAnswer(item, v)] }];
            }),
        ],
    });
    for (let pass = 0; pass <= calculated.length; pass += 1) {
        let changed = false;
        for (const item of calculated) {
            const expression = readCalculatedExpression(item);
            if (expression === undefined)
                continue;
            const v = toCalculatedValue(item, evaluateOne(engine, expression, questionnaire, working()));
            if (v === values.get(item.linkId))
                continue;
            changed = true;
            if (v === undefined)
                values.delete(item.linkId);
            else
                values.set(item.linkId, v);
        }
        if (!changed)
            break;
    }
    const ordered = new Map();
    for (const item of calculated) {
        const v = values.get(item.linkId);
        if (v !== undefined)
            ordered.set(item.linkId, v);
    }
    return ordered;
}
/** The FHIR answer for a draft value, as the renderer components write them. */
function draftAnswer(item, value) {
    if (value === undefined || value === null || value === '')
        return undefined;
    if (typeof value === 'object') {
        // A chosen AnswerOption (or an answer already in FHIR shape).
        const o = value;
        if (o.valueCoding !== undefined)
            return { valueCoding: o.valueCoding };
        if (o.valueString !== undefined)
            return { valueString: o.valueString };
        if (o.valueInteger !== undefined)
            return { valueInteger: o.valueInteger };
        if (o.valueDecimal !== undefined)
            return { valueDecimal: o.valueDecimal };
        if (o.valueBoolean !== undefined)
            return { valueBoolean: o.valueBoolean };
        return undefined;
    }
    if (typeof value === 'boolean')
        return { valueBoolean: value };
    if (typeof value === 'number') {
        return item.type === 'decimal' ? { valueDecimal: value } : { valueInteger: value };
    }
    if (typeof value !== 'string')
        return undefined;
    // A restored draft holds a choice as its code or string; map it back to
    // the option so its weight resolves.
    const option = item.answerOption?.find((o) => o.valueCoding?.code === value || o.valueString === value);
    if (option?.valueCoding !== undefined)
        return { valueCoding: option.valueCoding };
    if (item.type === 'integer') {
        const n = Number(value);
        return Number.isInteger(n) ? { valueInteger: n } : undefined;
    }
    if (item.type === 'decimal') {
        const n = Number(value);
        return Number.isFinite(n) ? { valueDecimal: n } : undefined;
    }
    if (item.type === 'date')
        return { valueDate: value };
    if (item.type === 'dateTime')
        return { valueDateTime: value };
    if (item.type === 'time')
        return { valueTime: value };
    return { valueString: value };
}
/**
 * Calculated values for the renderer's draft (`linkId → value`, as the
 * renderer components write them). Items with no value yet are absent, as
 * is everything with no engine (`null`; the default is the loaded one).
 */
export function computeDraftCalculations(questionnaire, draft, engine = loadedEngine ?? null) {
    const calculated = new Set(calculatedItems(questionnaire).map((i) => i.linkId));
    if (calculated.size === 0 || engine === null)
        return new Map();
    const item = [];
    const walk = (items) => {
        for (const q of items ?? []) {
            walk(q.item);
            if (q.type === 'group' || q.type === 'display' || calculated.has(q.linkId))
                continue;
            const raw = draft[q.linkId];
            const answer = (Array.isArray(raw) ? raw : [raw])
                .map((v) => draftAnswer(q, v))
                .filter((a) => a !== undefined);
            if (answer.length > 0)
                item.push({ linkId: q.linkId, answer });
        }
    };
    walk(questionnaire.item);
    return compute(engine, questionnaire, {
        resourceType: 'QuestionnaireResponse',
        status: 'in-progress',
        item,
    });
}
/**
 * Return `response` with every calculated item's answer recomputed from the
 * other answers. A client-supplied answer for a calculated item is discarded
 * and never seen by the expressions; computed answers are appended at the top
 * level in document order (responses are flat; see the web intake store).
 * With no engine (`null`; the default is the loaded one, if any) the
 * calculated answers are only stripped. The input is
 * not mutated.
 */
export function applyCalculations(questionnaire, response, engine = loadedEngine ?? null) {
    const calculated = calculatedItems(questionnaire);
    if (calculated.length === 0)
        return response;
    const stripped = {
        ...response,
        item: withoutItems(response.item, new Set(calculated.map((i) => i.linkId))),
    };
    if (engine === null)
        return stripped;
    const values = compute(engine, questionnaire, stripped);
    const byLinkId = new Map(calculated.map((i) => [i.linkId, i]));
    const item = [...(stripped.item ?? [])];
    for (const [linkId, v] of values) {
        const q = byLinkId.get(linkId);
        if (q !== undefined)
            item.push({ linkId, answer: [toAnswer(q, v)] });
    }
    return { ...stripped, item };
}
//# sourceMappingURL=calculation.js.map